import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  Send,
  XCircle,
  RotateCcw,
  AlertTriangle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Eraser,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Pencil,
  StickyNote,
  Save,
  Check,
  X,
} from "lucide-react";

import Sidebar from "../components/ui/Sidebar";
import { ocrApi } from "../../services/ocrApi";
import { ExtractionStageChip } from "../components/documents/ExtractionStageChip";
import { VerificationStatusBadges } from "../components/documents/VerificationStatusBadges";
import { FieldItemPanel } from "../components/documents/FieldItemPanel";
import {
  LineItemPanel,
  editKey,
  type TableEdits,
} from "../components/documents/LineItemPanel";
import { fieldEditKey, type FieldEdits } from "../components/documents/FieldItemPanel";
import {
  DocumentImageWithHighlight,
  type FieldMarker,
} from "../components/documents/DocumentImageWithHighlight";
import { FieldConnectingLine } from "../components/documents/FieldConnectingLine";
import { MarkerInlineEditor } from "../components/documents/MarkerInlineEditor";
import { HeaderMappingPopover } from "../components/documents/HeaderMappingPopover";
import { FieldRuleInlineEditor } from "../components/documents/FieldRuleInlineEditor";
import { OcrTextPanel } from "../components/documents/OcrTextPanel";
import type {
  ToolsMode,
  RuleFlags,
} from "../components/documents/ToolsViewConfiguration";
import { parseVerificationStatus } from "../../types/documents";
import type { VerificationStatusMap } from "../../types/documents";
import { useDocumentNavigator } from "../hooks/useDocumentNavigator";
import { useDocumentPan } from "../hooks/useDocumentPan";
import { useResolveOcrAccount } from "../hooks/useResolveOcrAccount";
import { useOcrTrackingMessages } from "../../hooks/useOcrTrackingMessages";
import { FRAME_TYPES } from "../../socket/constants";
import type { OcrTrackingFrame } from "../../types/documents";
import { FieldEditPopover } from "../components/documents/FieldEditPopover";
import {
  LayoutRulesViewer,
  type RulesViewerTab,
} from "../components/documents/LayoutRulesViewer";

const PARAM_DEFAULTS = {
  verification_component:
    (import.meta.env.VITE_OCR_VERIFICATION_COMPONENT as string | undefined) ?? "TV",
  table_name:
    (import.meta.env.VITE_OCR_TABLE_NAME as string | undefined) ??
    "doc_extraction_tracking",
};

interface PageData {
  image_path?: string;
  image_size?: string;
  extracted_content?: unknown;
  [key: string]: unknown;
}

type TabKey = "fielditem" | "lineitem";

/** Red table overlay for Line Item Rule mode. */
interface TableOverlay {
  x: number;
  y: number;
  width: number;
  height: number;
  rowHeights: number[];
  colWidths: number[];
}

interface FieldRulePair {
  whole: { x: number; y: number; w: number; h: number };
  key?: { x: number; y: number; w: number; h: number };
  value?: { x: number; y: number; w: number; h: number };
  field: string;
  confirmed?: boolean;
}

const DEFAULT_ORG_ID = "1234";

type GeomBox = { x: number; y: number; w: number; h: number };
type OcrWord = { text: string; x: number; y: number; w: number; h: number };

function normalizeOcrToken(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function unionBoxes(boxes: GeomBox[]): GeomBox | null {
  if (boxes.length === 0) return null;
  let xmin = Infinity;
  let ymin = Infinity;
  let xmax = -Infinity;
  let ymax = -Infinity;
  for (const b of boxes) {
    if (b.x < xmin) xmin = b.x;
    if (b.y < ymin) ymin = b.y;
    if (b.x + b.w > xmax) xmax = b.x + b.w;
    if (b.y + b.h > ymax) ymax = b.y + b.h;
  }
  return { x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin };
}

function wordCenterInBox(word: OcrWord, box: GeomBox): boolean {
  const cx = word.x + word.w / 2;
  const cy = word.y + word.h / 2;
  return (
    cx >= box.x &&
    cx <= box.x + box.w &&
    cy >= box.y &&
    cy <= box.y + box.h
  );
}

/**
 * Splits a single-box draw into refined key/value sub-regions using the
 * page's OCR words. When the user draws one box over "Country of origin:
 * INDIA" and picks "Country of Origin" as the attribute, we want the
 * backend to see key = the label words and value = "INDIA" — otherwise
 * its key/value subtraction heuristic ends up returning the wrong slice
 * ("origin:" instead of "INDIA"). Returns null when not enough confidence
 * to refine, so the caller can fall back to the synthetic half/whole pair.
 */
function refineSingleBoxFromOcr(
  whole: GeomBox,
  fieldName: string,
  ocrWords: OcrWord[],
): { key: GeomBox; value: GeomBox } | null {
  if (!fieldName || ocrWords.length === 0) return null;

  const insideWords = ocrWords.filter((w) => wordCenterInBox(w, whole));
  if (insideWords.length < 2) return null;

  const fieldTokens = fieldName
    .toLowerCase()
    .split(/\s+/)
    .map(normalizeOcrToken)
    .filter((t) => t.length > 0);
  if (fieldTokens.length === 0) return null;

  const keyWords: OcrWord[] = [];
  const valueWords: OcrWord[] = [];
  for (const w of insideWords) {
    const norm = normalizeOcrToken(w.text);
    if (!norm) continue;
    const isKey = fieldTokens.some(
      (t) => norm === t || (t.length >= 3 && norm.includes(t)),
    );
    if (isKey) keyWords.push(w);
    else valueWords.push(w);
  }

  if (keyWords.length === 0 || valueWords.length === 0) return null;

  const keyBox = unionBoxes(keyWords);
  const valueBox = unionBoxes(valueWords);
  if (!keyBox || !valueBox) return null;
  return { key: keyBox, value: valueBox };
}

export default function VerificationCockpitPage() {
  // Resolve the OCR X-User-Id once per session (POST /document_extraction/account).
  // Safe to call from multiple pages — react-query dedupes by key, so this
  // is a no-op when the inbox page has already populated the cache.
  // We also factor `ocrReady` into the `requestReady` gate below so every
  // verification query waits for the X-User-Id header to be available
  // before firing (otherwise the backend returns empty results).
  const { xUserId: resolvedXUserId } = useResolveOcrAccount();
  const ocrReady = resolvedXUserId != null;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const processId = searchParams.get("id") || "";
  const workflowId = searchParams.get("workflow_id") || "";
  const uploader = searchParams.get("uploader") || "";
  const filenameParam = searchParams.get("filename") || "";
  const layoutIdParam = searchParams.get("layout_id") || "";
  const verificationComponent =
    searchParams.get("verification_component") ||
    PARAM_DEFAULTS.verification_component;
  const tableName =
    searchParams.get("table_name") || PARAM_DEFAULTS.table_name;

  // Return path depends on where the user came from — config_studio_tracking
  // docs were opened from the Template Engine, everything else from the
  // OCR Tracking inbox. Carrying the table_name forward in the URL is what
  // lets the cockpit make this decision without extra state.
  const isConfigStudio = tableName === "config_studio_tracking";
  const basePath = isConfigStudio ? "/studio/templateEngine" : "/ocr-tracking";
  const inboxPath = basePath;
  const businessRulesPath = `${basePath}/business-rules`;
  // Helper to append the table_name onto downstream nav URLs so the next
  // page (Business Rules, Final Checker, etc.) keeps the same context.
  const withTableName = (params: URLSearchParams) => {
    if (tableName) params.set("table_name", tableName);
    return params;
  };

  // `requestReady` gates every `useQuery({ enabled: requestReady })` on this
  // page. Now AND'd with `ocrReady` so all verification fetches wait until
  // the /account resolve completes and `_resolvedXUserId` is populated.
  const requestReady =
    !!processId && !!workflowId && !!uploader && ocrReady;

  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [viewMode, setViewMode] = useState<"annotated" | "original" | "text">(
    "annotated",
  );
  const [activeTab, setActiveTab] = useState<TabKey>("fielditem");
  const [activeDataCell, setActiveDataCell] = useState<{
    tableName: string;
    rowIndex: number;
    column: string;
    boundary: string;
  } | null>(null);

  type ActiveHighlight =
    | {
      kind: "fielditem";
      boundary: string;
      group: string;
      fieldName: string;
      originalValue: string;
    }
    | {
      kind: "lineitem";
      boundary: string;
      tableName: string;
      rowIndex: number;
      column: string;
      originalValue: string;
    };
  const [active, setActive] = useState<ActiveHighlight | null>(null);
  const activeBoundary = active?.boundary ?? null;
  const [edits, setEdits] = useState<TableEdits>({});
  const [fieldEdits, setFieldEdits] = useState<FieldEdits>({});
  const [renames, setRenames] = useState<Record<string, string>>({});

  const [toolsMode, setToolsMode] = useState<Record<TabKey, ToolsMode>>({
    fielditem: "automatic",
    lineitem: "automatic",
  });
  const [ruleFlags, setRuleFlags] = useState<Record<TabKey, RuleFlags>>({
    fielditem: { singleBox: true, seperateBox: false, staticValue: false },
    lineitem: { singleBox: true, seperateBox: false, staticValue: false },
  });
  const [manualMode, setManualMode] = useState<Record<TabKey, boolean>>({
    fielditem: false,
    lineitem: false,
  });

  /**
   * Click-and-drag pan over the document image. Disabled while either
   * tab is in "rule" mode because that mode already handles mousedown
   * on the image (line-item table positioning, field-item rule
   * drawing) — letting both fire at once would steal the drag.
   *
   * Interactive descendants (data-interaction, buttons, inputs) are
   * gated inside the hook so highlight bbox drag, table-grid resize,
   * marker clicks etc. keep working.
   */
  const docPan = useDocumentPan<HTMLDivElement>({
    disabled:
      (activeTab === "fielditem" && toolsMode.fielditem === "rule") ||
      (activeTab === "lineitem" && toolsMode.lineitem === "rule"),
  });

  const [fieldRulePairs, setFieldRulePairs] = useState<FieldRulePair[]>([]);
  const [activeFieldPairIndex, setActiveFieldPairIndex] = useState<number | null>(null);
  // Field names whose marker badge should be suppressed locally because
  // the user cancelled the pair. The boundary clears via fieldRulePairs,
  // but markers are derived from coordinatesQuery / projection data and
  // would otherwise keep showing the numbered badge until the next
  // process / refetch.
  const [cancelledFieldNames, setCancelledFieldNames] = useState<Set<string>>(new Set());
  const [customFieldOptions, setCustomFieldOptions] = useState<string[]>([]);
  const [drawingSelection, setDrawingSelection] = useState<{
    start: { x: number; y: number } | null;
    end: { x: number; y: number } | null;
  }>({ start: null, end: null });

  const [selectedRow, setSelectedRow] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");

  const [tableOverlay, setTableOverlay] = useState<TableOverlay | null>(null);
  const [tableConfirmed, setTableConfirmed] = useState(false);
  const [imageNaturalDims, setImageNaturalDims] = useState<{ width: number; height: number } | null>(null);
  const [headerMappings, setHeaderMappings] = useState<Record<string, string>>({});

  // Field-item AND line-item rule boundaries are page-specific in the
  // reference (ocr-ui/.../tools/tools-file-preview.jsx re-derives `pairs`
  // and `table` from `currentPageData` per page). Without this reset,
  // boundaries drawn / loaded on page 1 carry over visually to page 2 / 3
  // because `loadCoordinates` and the preload effect only call
  // `setFieldRulePairs(pairs)` when the new page has data — they don't
  // wipe the previous page's pairs. We clear both here on page change so
  // each page starts fresh; the existing effects re-populate from the
  // page-specific projection / select_coordinates data when applicable.
  useEffect(() => {
    setFieldRulePairs([]);
    setActiveFieldPairIndex(null);
    setPendingFieldPair(null);
    setTableOverlay(null);
    setHeaderMappings({});
    setTableConfirmed(false);
    setActiveHeaderIndex(null);
    setSelectedRow("");
    setSelectedColumn("");
    setCancelledFieldNames(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab]);
  const [activeHeaderIndex, setActiveHeaderIndex] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [markerEdit, setMarkerEdit] = useState<{
    fieldName: string;
    keyBbox: { xmin: number; ymin: number; xmax: number; ymax: number };
  } | null>(null);
  const [pendingFieldPair, setPendingFieldPair] = useState<Partial<FieldRulePair> | null>(null);

  // View Rules — inline editor that replaces the right ConfigPanel.
  // Mirrors ocr-ui's ToolsView.jsx `isEditingRules` flow.
  const [rulesViewer, setRulesViewer] = useState<{
    open: boolean;
    tab: RulesViewerTab;
  }>({ open: false, tab: "lineitem" });

  /**
   * Load existing coordinates from the API when the page loads or tab changes.
   * This populates the field rule pairs with previously drawn boundaries.
   */
  useEffect(() => {
    if (!layoutIdParam || !requestReady) return;

    const loadCoordinates = async () => {
      try {
        const coordinatesType = activeTab === "fielditem" ? "fielditem" : "lineitem";
        const response = await ocrApi.selectCoordinates({
          coordinatesType,
          layoutId: layoutIdParam,
          pageNumber: currentPage,
        });

        console.log("[loadCoordinates] response:", response, "coordinatesType:", coordinatesType);
        console.log("[loadCoordinates] response keys:", Object.keys(response as any));

        if (!response || typeof response !== "object") return;

        const obj = response as Record<string, unknown>;
        const key = activeTab === "fielditem" ? "field_item_raw_coordinates" : "lineitem_raw_coordinates";
        let rawData = obj[key];

        console.log("[loadCoordinates] rawData:", rawData, "key:", key);
        console.log("[loadCoordinates] obj:", obj);

        // If rawData is undefined, check if the response IS the data itself
        if (!rawData && activeTab === "lineitem") {
          // Check if response has coordinates property directly
          if ((response as any)?.coordinates) {
            rawData = response;
          }
        }

        // For lineitem, the actual coordinates are nested under .coordinates property
        let coordinates = rawData;
        if (activeTab === "lineitem" && rawData && typeof rawData === "object") {
          coordinates = (rawData as any).coordinates;
        }

        console.log("[loadCoordinates] extracted coordinates:", coordinates);

        if (activeTab === "fielditem") {
          // Handle field item coordinates
          if (!Array.isArray(coordinates)) return;

          const pairs = coordinates.map((coord: any) => {
            // Handle both 'key' and 'key_coordinates' property names
            const keyCoord = coord.key || coord.key_coordinates;
            const valueCoord = coord.value || coord.value_coordinates;

            const keyBox = (keyCoord && Object.keys(keyCoord).length > 0) ? {
              x: keyCoord.xmin,
              y: keyCoord.ymin,
              w: keyCoord.xmax - keyCoord.xmin,
              h: keyCoord.ymax - keyCoord.ymin,
            } : undefined;

            const valueBox = (valueCoord && Object.keys(valueCoord).length > 0) ? {
              x: valueCoord.xmin,
              y: valueCoord.ymin,
              w: valueCoord.xmax - valueCoord.xmin,
              h: valueCoord.ymax - valueCoord.ymin,
            } : undefined;

            let wholeBox;
            if (keyBox && valueBox) {
              const xmin = Math.min(keyBox.x, valueBox.x);
              const ymin = Math.min(keyBox.y, valueBox.y);
              const xmax = Math.max(keyBox.x + keyBox.w, valueBox.x + valueBox.w);
              const ymax = Math.max(keyBox.y + keyBox.h, valueBox.y + valueBox.h);
              wholeBox = { x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin };
            } else if (keyBox) {
              wholeBox = keyBox;
            } else if (valueBox) {
              wholeBox = valueBox;
            } else {
              return null;
            }

            return {
              whole: wholeBox,
              key: keyBox,
              value: valueBox,
              field: coord.field_name || "",
              confirmed: true,
            } as FieldRulePair;
          }).filter((p): p is FieldRulePair => p !== null);

          if (pairs.length > 0) {
            setFieldRulePairs(pairs);
            console.log("[loadCoordinates] Loaded", pairs.length, "field item pairs");
          }
        } else if (activeTab === "lineitem") {
          // Handle line item coordinates - convert to table overlay
          console.log("[loadCoordinates] Processing lineitem coordinates:", coordinates);

          if (coordinates && typeof coordinates === "object") {
            const coordsObj = coordinates as any;
            const coords = coordsObj.table_id ? coordsObj.coordinates : coordsObj;

            console.log("[loadCoordinates] coords structure:", coords);

            if (coords && coords.header && coords.header.vertical_line) {
              const vLines = coords.header.vertical_line;
              const hHeaderLines = coords.header.horizontal_line || [];
              const hContentLines = coords.content?.horizontal_line || [];

              console.log("[loadCoordinates] vLines:", vLines, "hHeaderLines:", hHeaderLines, "hContentLines:", hContentLines);

              const allV = (Array.from(new Set(vLines.map((l: any) => Number(l.xmin)))) as number[]).sort((a, b) => a - b);
              const allH = (Array.from(new Set([...hHeaderLines, ...hContentLines].map((l: any) => Number(l.ymin)))) as number[]).sort((a, b) => a - b);

              console.log("[loadCoordinates] allV:", allV, "allH:", allH);

              if (allV.length >= 2 && allH.length >= 2) {
                const xMin = allV[0];
                const xMax = allV[allV.length - 1];
                const yMin = allH[0];
                const yMax = allH[allH.length - 1];

                const colWidths = [];
                for (let i = 0; i < allV.length - 1; i++) colWidths.push(allV[i + 1] - allV[i]);
                const rowHeights = [];
                for (let i = 0; i < allH.length - 1; i++) rowHeights.push(allH[i + 1] - allH[i]);

                const newMappings: Record<string, string> = {};
                vLines.forEach((line: any, idx: number) => {
                  if (idx < vLines.length - 1 && line.field_name) {
                    newMappings[`0_${idx}`] = line.field_name;
                  }
                });

                console.log("[loadCoordinates] Setting tableOverlay:", { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin, colWidths, rowHeights });
                setTableOverlay({
                  x: xMin,
                  y: yMin,
                  width: xMax - xMin,
                  height: yMax - yMin,
                  colWidths,
                  rowHeights,
                });
                setHeaderMappings(newMappings);
                setTableConfirmed(true);
                setSelectedRow(String(rowHeights.length - 1));
                setSelectedColumn(String(colWidths.length));
                console.log("[loadCoordinates] Loaded line item table overlay successfully");
              } else {
                console.log("[loadCoordinates] Not enough lines - allV.length:", allV.length, "allH.length:", allH.length);
              }
            } else {
              console.log("[loadCoordinates] coords structure missing header.vertical_line");
            }
          } else {
            console.log("[loadCoordinates] lineitem coordinates is not an object or is falsy");
          }
        }
      } catch (error) {
        console.error("[loadCoordinates] Error:", error);
      }
    };

    loadCoordinates();
  }, [layoutIdParam, requestReady, activeTab, currentPage]);

  /**
   * Recalculates the equal-spaced table structure when rows/cols change
   * in Rule mode, or initializes it if null.
   */
  useEffect(() => {
    if (activeTab !== "lineitem" || toolsMode.lineitem !== "rule") {
      return;
    }

    const rows = parseInt(selectedRow, 10);
    const cols = parseInt(selectedColumn, 10);

    if (isNaN(rows) || rows <= 0 || isNaN(cols) || cols <= 0 || !imageNaturalDims) {
      return;
    }

    setTableOverlay((prev) => {
      // If we already have a table (possibly preloaded or already started), 
      // only update it if the dimensions changed and we are NOT confirmed.
      if (prev && tableConfirmed) return prev;

      const totalRows = rows + 1;
      const totalCols = cols;

      if (prev) {
        if (prev.rowHeights.length === totalRows && prev.colWidths.length === totalCols) {
          return prev;
        }
        const totalWidth = prev.width;
        const totalHeight = prev.height;
        return {
          ...prev,
          rowHeights: Array(totalRows).fill(totalHeight / totalRows),
          colWidths: Array(totalCols).fill(totalWidth / totalCols),
        };
      } else {
        const width = imageNaturalDims.width * 0.6;
        const height = imageNaturalDims.height * 0.4;
        const x = (imageNaturalDims.width - width) / 2;
        const y = (imageNaturalDims.height - height) / 2;

        return {
          x,
          y,
          width,
          height,
          rowHeights: Array(totalRows).fill(height / totalRows),
          colWidths: Array(totalCols).fill(width / totalCols),
        };
      }
    });
  }, [selectedRow, selectedColumn, activeTab, toolsMode.lineitem, imageNaturalDims, tableConfirmed]);

  const homeQuery = useQuery({
    queryKey: ["ocr", "verification", "home", processId],
    queryFn: () =>
      ocrApi.verificationHome({
        processId,
        workflowId,
        uploader,
        verificationComponent,
        tableName,
      }),
    enabled: requestReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const homePayload = (homeQuery.data ?? null) as Record<string, unknown> | null;

  const pages = useMemo<Record<string, PageData> | null>(() => {
    if (!homePayload) return null;
    const direct = homePayload.pages;
    if (direct && typeof direct === "object" && !Array.isArray(direct)) {
      return direct as Record<string, PageData>;
    }
    const wrapped = (homePayload.data as Record<string, unknown> | undefined)?.pages;
    if (wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)) {
      return wrapped as Record<string, PageData>;
    }
    return null;
  }, [homePayload]);

  const totalPages = useMemo<number>(() => {
    if (!pages) return 1;
    const numericKeys = Object.keys(pages)
      .map((k) => parseInt(k, 10))
      .filter((n) => !Number.isNaN(n));
    return numericKeys.length > 0 ? Math.max(...numericKeys) : 1;
  }, [pages]);

  const pageData: PageData | null = pages?.[String(currentPage)] ?? null;

  const collectionName =
    activeTab === "fielditem" ? "fielditem_content" : "lineitem_content";

  const projectionQuery = useQuery({
    queryKey: [
      "ocr",
      "verification",
      "projection",
      collectionName,
      processId,
      currentPage,
      verificationComponent,
      tableName,
    ],
    queryFn: () =>
      ocrApi.selectProjection({
        processId,
        collectionName,
        pageNumber: currentPage,
        verificationComponent,
        tableName,
      }),
    enabled: requestReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  function pickExtractedContent(payload: unknown): unknown {
    if (payload === null || payload === undefined) return undefined;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        const ec = pickExtractedContent(item);
        if (ec !== undefined) return ec;
      }
      return undefined;
    }
    if (typeof payload !== "object") return undefined;
    const obj = payload as Record<string, unknown>;
    if (obj.data !== undefined && obj.extracted_content === undefined) {
      const fromData = pickExtractedContent(obj.data);
      if (fromData !== undefined) return fromData;
    }
    const direct = obj[String(currentPage)];
    if (direct && typeof direct === "object") {
      const ec = (direct as Record<string, unknown>).extracted_content;
      if (ec !== undefined) return ec;
      if ((direct as Record<string, unknown>).fielditem !== undefined) return direct;
    }
    if (obj.extracted_content !== undefined) return obj.extracted_content;
    if (obj.fielditem !== undefined) return obj;
    // Preserve the wrapping object — handing `tables[0]` back makes
    // LineItemPanel's parser miss the table because it expects either
    // `{tables: [...]}` or an array of table objects.
    if (Array.isArray(obj.tables) && obj.tables.length > 0) return obj;
    if (obj.lineitem_raw_coordinates !== undefined || obj.fielditem_raw_coordinates !== undefined) return obj;
    return undefined;
  }

  const tabData = useMemo<unknown>(
    () => pickExtractedContent(projectionQuery.data),
    [projectionQuery.data, currentPage],
  );

  const ocrWords = useMemo(() => {
    if (!pageData || typeof pageData !== "object" || !imageNaturalDims) return [];
    const obj = pageData as any;
    const rawWords = (obj.words as any[]) || (obj.ocr?.words as any[]) || [];

    return rawWords.map(w => {
      const x = Number(w.xmin ?? w.x ?? 0);
      const y = Number(w.ymin ?? w.y ?? 0);
      const x2 = Number(w.xmax ?? (Number(w.x ?? 0) + Number(w.w ?? 0)));
      const y2 = Number(w.ymax ?? (Number(w.y ?? 0) + Number(w.h ?? 0)));

      return {
        text: String(w.text || w.value || w.content || ""),
        x,
        y,
        w: Math.max(0, x2 - x),
        h: Math.max(0, y2 - y)
      };
    }).filter(w => w.text && w.w > 0);
  }, [pageData, imageNaturalDims]);

  // Preload coordinates from backend (Parity with eroyal preloader)
  useEffect(() => {
    if (!tabData || !imageNaturalDims) return;

    if (activeTab === "fielditem" && fieldRulePairs.length === 0) {
      const raw = (tabData as any).fielditem_raw_coordinates;
      if (Array.isArray(raw) && raw.length > 0) {
        const preloaded = raw.map((item: any): FieldRulePair | null => {
          const k = item.key || item.key_coordinates;
          const v = item.value || item.value_coordinates;
          if (!k && !v) return null;

          const whole = item.whole || k || v;
          return {
            whole: { x: whole.xmin, y: whole.ymin, w: whole.xmax - whole.xmin, h: whole.ymax - whole.ymin },
            key: k ? { x: k.xmin, y: k.ymin, w: k.xmax - k.xmin, h: k.ymax - k.ymin } : undefined,
            value: v ? { x: v.xmin, y: v.ymin, w: v.xmax - v.xmin, h: v.ymax - v.ymin } : undefined,
            field: item.field_name || "",
            confirmed: true,
          };
        }).filter((p): p is FieldRulePair => p !== null);
        if (preloaded.length > 0) setFieldRulePairs(preloaded);
      }
    } else if (activeTab === "lineitem" && !tableOverlay) {
      const rawLineData = (tabData as any).lineitem_raw_coordinates;
      if (rawLineData) {
        const coords = rawLineData.table_id ? rawLineData.coordinates : rawLineData;
        if (coords && coords.header && coords.header.vertical_line) {
          const vLines = coords.header.vertical_line;
          const hHeaderLines = coords.header.horizontal_line || [];
          const hContentLines = coords.content?.horizontal_line || [];

          const allV = (Array.from(new Set(vLines.map((l: any) => Number(l.xmin)))) as number[]).sort((a, b) => a - b);
          const allH = (Array.from(new Set([...hHeaderLines, ...hContentLines].map((l: any) => Number(l.ymin)))) as number[]).sort((a, b) => a - b);

          if (allV.length >= 2 && allH.length >= 2) {
            const xMin = allV[0];
            const xMax = allV[allV.length - 1];
            const yMin = allH[0];
            const yMax = allH[allH.length - 1];

            const colWidths = [];
            for (let i = 0; i < allV.length - 1; i++) colWidths.push(allV[i + 1] - allV[i]);
            const rowHeights = [];
            for (let i = 0; i < allH.length - 1; i++) rowHeights.push(allH[i + 1] - allH[i]);

            const newMappings: Record<string, string> = {};
            vLines.forEach((line: any, idx: number) => {
              if (idx < vLines.length - 1 && line.field_name) {
                newMappings[`0_${idx}`] = line.field_name;
              }
            });

            setTableOverlay({
              x: xMin,
              y: yMin,
              width: xMax - xMin,
              height: yMax - yMin,
              colWidths,
              rowHeights,
            });
            setHeaderMappings(newMappings);
            setTableConfirmed(true);

            // Sync dropdowns if possible
            setSelectedRow(String(rowHeights.length - 1));
            setSelectedColumn(String(colWidths.length));
          }
        }
      }
    }
  }, [tabData, activeTab, imageNaturalDims, fieldRulePairs.length, tableOverlay]);

  const handleRuleSelectionChange = (selection: {
    selectedRow: string;
    selectedColumn: string;
  }) => {
    setSelectedRow(selection.selectedRow);
    setSelectedColumn(selection.selectedColumn);
    setTableConfirmed(false); // Reset confirmation when selection changes
  };

  // Use refs to store drawing coordinates to avoid React state batching issues
  const drawingStartRef = useRef<{ x: number; y: number } | null>(null);
  const drawingEndRef = useRef<{ x: number; y: number } | null>(null);

  // Field Rule Drawing Handlers
  const handleDrawingStart = (x: number, y: number) => {
    console.log("[handleDrawingStart] Setting start point:", { x, y });
    // Set refs immediately (synchronous)
    drawingStartRef.current = { x, y };
    drawingEndRef.current = { x, y };
    // Also update state for visual feedback
    setDrawingSelection({ start: { x, y }, end: { x, y } });
    setActiveFieldPairIndex(null);
  };

  const handleDrawingMove = (x: number, y: number) => {
    // Update ref immediately (synchronous)
    drawingEndRef.current = { x, y };
    // Also update state for visual feedback
    setDrawingSelection((prev) => {
      if (!prev.start) return prev;
      return { start: prev.start, end: { x, y } };
    });
  };

  const handleDrawingEnd = (endX: number, endY: number) => {
    // Read start from ref, end is passed directly from mouseup event
    const start = drawingStartRef.current;

    console.log("[handleDrawingEnd] start:", start, "end:", { x: endX, y: endY });

    if (!start) {
      setDrawingSelection({ start: null, end: null });
      drawingStartRef.current = null;
      drawingEndRef.current = null;
      return;
    }

    const box = {
      x: Math.min(start.x, endX),
      y: Math.min(start.y, endY),
      w: Math.abs(endX - start.x),
      h: Math.abs(endY - start.y),
    };

    console.log("[handleDrawingEnd] box:", box, "ruleFlags:", ruleFlags.fielditem);

    // Clear refs
    drawingStartRef.current = null;
    drawingEndRef.current = null;
    setDrawingSelection({ start: null, end: null });

    if (box.w < 5 || box.h < 5) {
      return;
    }

    const { singleBox, seperateBox, staticValue } = ruleFlags.fielditem;

    if (seperateBox) {
      if (!pendingFieldPair) {
        // Step 1: Draw Key
        setPendingFieldPair({ key: box, whole: box });
        toast.info("Key area set. Now draw the Value area.");
      } else {
        // Step 2: Draw Value
        const newPair: FieldRulePair = {
          key: pendingFieldPair.key,
          value: box,
          whole: {
            x: Math.min(pendingFieldPair.key!.x, box.x),
            y: Math.min(pendingFieldPair.key!.y, box.y),
            w: Math.max(pendingFieldPair.key!.x + pendingFieldPair.key!.w, box.x + box.w) - Math.min(pendingFieldPair.key!.x, box.x),
            h: Math.max(pendingFieldPair.key!.y + pendingFieldPair.key!.h, box.y + box.h) - Math.min(pendingFieldPair.key!.y, box.y),
          },
          field: "",
          confirmed: false,
        };
        setFieldRulePairs((prev) => {
          const next = [...prev, newPair];
          setActiveFieldPairIndex(next.length - 1);
          return next;
        });
        setPendingFieldPair(null);
      }
    } else if (singleBox) {
      // Single Box: Split into Key (left half) and the whole drawn area
      const keyBox = {
        x: box.x,
        y: box.y,
        w: box.w / 2,
        h: box.h,
      };
      const newPair: FieldRulePair = {
        whole: box,
        key: keyBox,
        value: undefined,
        field: "",
        confirmed: false,
      };
      setFieldRulePairs((prev) => {
        const next = [...prev, newPair];
        console.log("[handleDrawingEnd] singleBox - pairs count:", next.length, "newPair:", newPair);
        setActiveFieldPairIndex(next.length - 1);
        return next;
      });
    } else if (staticValue) {
      // Static Value / Only Value: No key box
      const newPair: FieldRulePair = {
        whole: box,
        key: { x: 0, y: 0, w: 0, h: 0 }, // Represents empty key
        value: undefined,
        field: "",
        confirmed: false,
      };
      setFieldRulePairs((prev) => {
        const next = [...prev, newPair];
        console.log("[handleDrawingEnd] staticValue - pairs count:", next.length);
        setActiveFieldPairIndex(next.length - 1);
        return next;
      });
    } else {
      // Fallback: Default to singleBox behavior
      console.warn("[handleDrawingEnd] No flag matched, using default singleBox behavior");
      const keyBox = {
        x: box.x,
        y: box.y,
        w: box.w / 2,
        h: box.h,
      };
      const newPair: FieldRulePair = {
        whole: box,
        key: keyBox,
        value: undefined,
        field: "",
        confirmed: false,
      };
      setFieldRulePairs((prev) => {
        const next = [...prev, newPair];
        console.log("[handleDrawingEnd] fallback - pairs count:", next.length);
        setActiveFieldPairIndex(next.length - 1);
        return next;
      });
    }

    toast.success("Area captured. Select field name.");
  };

  const handleAddNewFieldAttribute = (newValue: string) => {
    setCustomFieldOptions(prev => prev.includes(newValue) ? prev : [...prev, newValue]);
  };

  const handleFieldRuleConfirm = (fieldName: string) => {
    if (activeFieldPairIndex === null) return;
    setFieldRulePairs((prev) => {
      const updated = [...prev];
      updated[activeFieldPairIndex] = { ...updated[activeFieldPairIndex], field: fieldName, confirmed: true };
      console.log("[handleFieldRuleConfirm] Updated pairs:", updated);
      return updated;
    });
    // Re-confirming a previously cancelled field should bring its marker
    // back, so drop it from the suppression set.
    setCancelledFieldNames((prev) => {
      if (!prev.has(fieldName)) return prev;
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
    setActiveFieldPairIndex(null);
    toast.success(`Rule set for "${fieldName}"`);
  };

  const handleFieldRuleCancel = () => {
    setPendingFieldPair(null);
    if (activeFieldPairIndex === null) return;
    // Cancel always discards the active pair — both fresh draws and
    // existing saved pairs. The earlier `!confirmed` guard relied on
    // `onFieldRuleSelect` flipping confirmed=false on click, which is
    // racy (some click paths don't flip the flag before Cancel fires),
    // leaving the boundary stuck on screen until clicked again.
    const removedField = fieldRulePairs[activeFieldPairIndex]?.field ?? "";
    setFieldRulePairs((prev) => {
      // Remove the active pair AND any other pair sharing the same field
      // name. Duplicates show up as "two layers" of the same boundary —
      // the API and projection sources have been seen to disagree, and
      // a second click to clear "the other layer" was masking the dupes.
      const next = prev.filter((p, i) => {
        if (i === activeFieldPairIndex) return false;
        if (removedField && p.field === removedField) return false;
        return true;
      });
      return next;
    });
    // The numbered badge for this field is drawn by the markers layer
    // (sourced from coordinatesQuery / projection), not by fieldRulePairs,
    // so filtering the pair alone leaves the number hovering at the same
    // spot. Track the name locally so the markers useMemo skips it.
    if (removedField) {
      setCancelledFieldNames((prev) => {
        const next = new Set(prev);
        next.add(removedField);
        return next;
      });
    }
    setActiveFieldPairIndex(null);
  };

  const confirmLineItemRule = () => {
    setTableConfirmed(true);
    toast.success("Table rule confirmed. Click 'Process' to extract data.");
  };

  // Discards the in-progress (unconfirmed) line item table draw. Mirrors
  // the Cancel button on field-item rule pairs so the line-item flow has
  // matching escape hatch.
  const cancelLineItemRule = () => {
    setTableOverlay(null);
    setTableConfirmed(false);
    setHeaderMappings({});
    setActiveHeaderIndex(null);
    setSelectedRow("");
    setSelectedColumn("");
  };

  const handleWordClick = (text: string) => {
    // 1. Line Item Header Mapping
    if (activeHeaderIndex) {
      setHeaderMappings(prev => ({
        ...prev,
        [`${activeHeaderIndex.rowIndex}_${activeHeaderIndex.colIndex}`]: text
      }));
      toast.info(`Header set to "${text}"`);
      return;
    }

    // 2. Field Rule Attribute Mapping (Rule Mode)
    if (activeFieldPairIndex !== null) {
      setFieldRulePairs(prev => {
        const updated = [...prev];
        updated[activeFieldPairIndex] = { ...updated[activeFieldPairIndex], field: text };
        return updated;
      });
      toast.info(`Attribute set to "${text}"`);
      return;
    }

    // 3. Field Item Value Editing (Data Mode)
    if (active && active.kind === "fielditem") {
      setFieldEdits(prev => ({
        ...prev,
        [active.boundary]: text
      }));
      toast.info(`Value set to "${text}"`);
      return;
    }
  };

  const isDirty =
    Object.keys(edits).length > 0 ||
    Object.keys(fieldEdits).length > 0 ||
    Object.keys(renames).length > 0;

  const navigateToPage = (next: number) => {
    const totalEdits = Object.keys(edits).length + Object.keys(fieldEdits).length;
    if (isDirty) {
      const choice = window.confirm(
        `You have ${totalEdits} unsaved edit(s) on page ${currentPage}. Save before moving to page ${next}?`,
      );
      if (choice) {
        saveMutation.mutate(undefined, {
          onSuccess: () => setCurrentPage(next),
        });
        return;
      }
      setEdits({});
      setFieldEdits({});
      setRenames({});
    }
    setActiveDataCell(null);
    setActive(null);
    setCurrentPage(next);
  };

  const coordinatesQuery = useQuery({
    queryKey: [
      "ocr",
      "verification",
      "coordinates",
      activeTab,
      layoutIdParam,
      currentPage,
    ],
    queryFn: () =>
      ocrApi.selectCoordinates({
        coordinatesType: activeTab,
        layoutId: layoutIdParam,
        pageNumber: currentPage,
      }),
    enabled: requestReady && !!layoutIdParam,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Live updates from the OCR ws-server scoped to this process_id.
  // Mirrors ocr-ui/src/modules/document-extraction/tools/ToolsView.jsx —
  // when the pipeline finishes a field_item or line_item extraction for
  // the document this page is showing, refetch the projection (and
  // coordinates, since lineitem completions can shift the layout id) and
  // notify the user. The hook filters frames by process_id and user_id;
  // we don't restrict by table_name so completions on either tracking
  // table (doc_extraction_tracking / config_studio_tracking) flow through.
  //
  // Toast deduplication: the backend emits 2–3 frames per logical
  // extraction event (e.g. one `update` + one `process_complete` for
  // line_item, plus a generic `process_complete` summary). Without
  // de-dup the user sees 3 toasts stacked for a single retrigger. We
  // track the last toast timestamp per tab in a ref and suppress
  // duplicate toasts within a 3-second window — refetches still fire
  // on every frame since they're idempotent through React Query.
  const lastToastAtRef = useRef<{ fielditem: number; lineitem: number }>({
    fielditem: 0,
    lineitem: 0,
  });
  const TOAST_DEDUPE_MS = 3000;

  // Completion signal — flipped by the WS handler when the backend emits
  // `process_complete` (or an `update` frame) for the active tab. The
  // processMutation polling loop checks this each tick and breaks early
  // so the "Processing…" state ends as soon as the socket confirms the
  // extraction, instead of waiting for a poll iteration where the
  // select_projection response happens to match the `hasContent` shape.
  // Reset at the start of each processMutation run.
  const extractionCompleteRef = useRef<{ fielditem: boolean; lineitem: boolean }>({
    fielditem: false,
    lineitem: false,
  });
  useOcrTrackingMessages(
    (frame: OcrTrackingFrame) => {
      const isFieldItem =
        frame.type === FRAME_TYPES.FIELD_ITEM ||
        (activeTab === "fielditem" && frame.action === "update");
      const isLineItem =
        frame.type === FRAME_TYPES.LINE_ITEM ||
        (activeTab === "lineitem" && frame.action === "update");
      const isCompletion =
        frame.action === "update" || frame.action === "process_complete";
      if (!isCompletion) return;
      if (!isFieldItem && !isLineItem) return;
      // Only react to the tab currently visible — otherwise the user
      // sees a phantom refresh / spurious toast on the other tab.
      if (isFieldItem && activeTab !== "fielditem") return;
      if (isLineItem && activeTab !== "lineitem") return;

      projectionQuery.refetch();
      if (layoutIdParam) coordinatesQuery.refetch();

      const tabKey: "fielditem" | "lineitem" = isLineItem
        ? "lineitem"
        : "fielditem";

      // Tell the processMutation polling loop (if running) the backend is
      // done. Without this, the loop keeps polling select_projection until
      // either its hasContent check matches or it hits maxAttempts — so the
      // "Processing…" UI stays up even though the data has already arrived
      // via the socket.
      extractionCompleteRef.current[tabKey] = true;
      const now = Date.now();
      if (now - lastToastAtRef.current[tabKey] < TOAST_DEDUPE_MS) {
        // Same logical event — a follow-up frame in the same burst. The
        // refetch above is enough; skip the duplicate toast.
        return;
      }
      lastToastAtRef.current[tabKey] = now;

      const message =
        frame.message ||
        (isLineItem
          ? "Line item extraction completed."
          : "Field extraction completed.");
      toast.success(message);
    },
    { processId },
  );

  const markers = useMemo<FieldMarker[]>(() => {
    // Derive markers directly from `fieldRulePairs` so the numbered
    // badges share the same page-scoped lifecycle as the boundary
    // rectangles. This mirrors the reference (ocr-ui/.../tools-file-
    // preview.jsx ~L1892), where `pairs.map((pair, index) => ... {index +
    // 1})` is the single source of truth for both the rendered boxes
    // and their numbered badges.
    //
    // Earlier the markers were sourced from `coordinatesQuery.data`
    // and the projection's `*_raw_coordinates` block, both of which
    // can hold the whole layout's field list. That leaked layout-wide
    // entries onto pages that had no boundaries (e.g. page 2 rendered
    // numbers 11..15 simply because the layout defined 15 fields).
    // Routing through `fieldRulePairs` — which `loadCoordinates`
    // refills per page and the page-change reset effect clears —
    // keeps the two views in lock-step.
    if (activeTab !== "fielditem") return [];
    const out: FieldMarker[] = [];
    fieldRulePairs.forEach((pair) => {
      const fieldName = pair.field || "";
      if (!fieldName) return;
      if (cancelledFieldNames.has(fieldName)) return;
      // Prefer the explicit key box; fall back to the whole-pair box
      // (used for "static value" / "value only" pairs that have no key).
      const src =
        pair.key && pair.key.w > 0 && pair.key.h > 0 ? pair.key : pair.whole;
      if (!src || !(src.w > 0) || !(src.h > 0)) return;
      out.push({
        index: out.length + 1,
        fieldName,
        keyBbox: {
          xmin: src.x,
          ymin: src.y,
          xmax: src.x + src.w,
          ymax: src.y + src.h,
        },
      });
    });
    return out;
  }, [fieldRulePairs, activeTab, cancelledFieldNames]);

  // True when the current page's projection actually carries line-item
  // rows. `select_coordinates` is keyed by layout_id, so it returns the
  // same table outline for every page — without this guard the
  // automatic-mode overlay drawn on page 1 keeps showing on pages that
  // don't actually have a table. Rule mode is exempt (the user is
  // drawing a boundary, not viewing extracted rows).
  const lineItemHasRowsOnPage = useMemo(() => {
    if (activeTab !== "lineitem" || !tabData) return false;
    const obj = tabData as Record<string, unknown>;
    const tables = obj.tables;
    if (!Array.isArray(tables)) return false;
    return tables.some((t) => {
      const tobj = t as Record<string, unknown> | undefined;
      const rows = (tobj?.table_rows ?? tobj?.rows) as unknown[] | undefined;
      return Array.isArray(rows) && rows.length > 0;
    });
  }, [activeTab, tabData]);

  // Local line-item table derived from `select_coordinates` + the page's
  // OCR words. Used as a fallback when `select_projection` hasn't
  // returned rows yet (or returns empty `{tables: []}`) — the coordinates
  // response has the column names and row boundaries, and we already
  // have OCR text bounding boxes, so we can fill each cell client-side.
  const lineItemPreview = useMemo<{
    columns: string[];
    rows: Record<string, string>[];
  } | null>(() => {
    const data = coordinatesQuery.data as Record<string, unknown> | undefined;
    if (!data) return null;
    const raw = data.lineitem_raw_coordinates as Record<string, unknown> | undefined;
    if (!raw) return null;
    const coords = (raw.coordinates as Record<string, unknown> | undefined) ?? raw;
    if (!coords || typeof coords !== "object") return null;
    const header = (coords as Record<string, unknown>).header as
      | Record<string, unknown>
      | undefined;
    const content = (coords as Record<string, unknown>).content as
      | Record<string, unknown>
      | undefined;

    const vLinesRaw = header?.vertical_line;
    if (!Array.isArray(vLinesRaw) || vLinesRaw.length < 2) return null;

    // Sort vertical lines by xmin so columns align with positions.
    const sortedVLines = [...vLinesRaw]
      .filter((l) => Number.isFinite(Number((l as Record<string, unknown>)?.xmin)))
      .sort(
        (a, b) =>
          Number((a as Record<string, unknown>).xmin) -
          Number((b as Record<string, unknown>).xmin),
      );
    if (sortedVLines.length < 2) return null;

    const xs = sortedVLines.map((l) =>
      Number((l as Record<string, unknown>).xmin),
    );

    // N columns ↔ N+1 vertical lines; field_name on line i names column i.
    const columns: string[] = [];
    for (let i = 0; i < sortedVLines.length - 1; i++) {
      const line = sortedVLines[i] as Record<string, unknown> | undefined;
      const name = typeof line?.field_name === "string" ? line.field_name : "";
      columns.push(name || `Column ${i + 1}`);
    }

    // Row boundaries come from content.horizontal_line (deduped). With N
    // consecutive lines we get N-1 rows between them.
    const hContentRaw = (content?.horizontal_line as unknown[] | undefined) ?? [];
    const rawYs = hContentRaw
      .map((l) => Number((l as Record<string, unknown>)?.ymin))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
    const DEDUPE_TOL = 3;
    const ys: number[] = [];
    for (const y of rawYs) {
      if (ys.length === 0 || y - ys[ys.length - 1] > DEDUPE_TOL) {
        ys.push(y);
      }
    }
    if (ys.length < 2) return { columns, rows: [] };

    const rowBands: Array<{ top: number; bottom: number }> = [];
    for (let i = 0; i < ys.length - 1; i++) {
      const top = ys[i];
      const bottom = ys[i + 1];
      if (bottom - top > DEDUPE_TOL) rowBands.push({ top, bottom });
    }
    if (rowBands.length === 0) return { columns, rows: [] };

    const colBands: Array<{ left: number; right: number }> = [];
    for (let i = 0; i < xs.length - 1; i++) {
      colBands.push({ left: xs[i], right: xs[i + 1] });
    }

    // For each row × col cell, collect OCR words whose center falls
    // inside the cell, then join their text in reading order.
    const rows: Record<string, string>[] = rowBands.map((rb) => {
      const out: Record<string, string> = {};
      colBands.forEach((cb, colIdx) => {
        const colName = columns[colIdx];
        const cellWords = ocrWords
          .filter((w) => {
            const cx = w.x + w.w / 2;
            const cy = w.y + w.h / 2;
            return (
              cx >= cb.left &&
              cx <= cb.right &&
              cy >= rb.top &&
              cy <= rb.bottom
            );
          })
          .sort((a, b) => {
            const dy = a.y - b.y;
            if (Math.abs(dy) > Math.min(a.h, b.h) * 0.5) return dy;
            return a.x - b.x;
          });
        out[colName] = cellWords.map((w) => w.text).join(" ").trim();
      });
      return out;
    });

    // Keep only rows where at least one cell has content. Empty rows
    // would happen when `pageData.words` isn't populated for that page.
    const populatedRows = rows.filter((r) =>
      Object.values(r).some((v) => v && v.length > 0),
    );

    return { columns, rows: populatedRows };
  }, [coordinatesQuery.data, ocrWords]);

  const fieldItemSuggestionsQuery = useQuery({
    queryKey: ["ocr", "verification", "suggestions", "field_item"],
    queryFn: () => ocrApi.fieldsAttributeIdentifierList({ sectionType: "field_item" }),
    enabled: requestReady,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const lineItemSuggestionsQuery = useQuery({
    queryKey: ["ocr", "verification", "suggestions", "line_item"],
    queryFn: () => ocrApi.fieldsAttributeIdentifierList({ sectionType: "line_item" }),
    enabled: requestReady,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const fieldAttributeOptions = useMemo(() => {
    const raw: any = fieldItemSuggestionsQuery.data;
    if (!raw) return [];
    let list: any[] = [];
    if (Array.isArray(raw.data)) list = raw.data;
    else if (Array.isArray(raw.status)) list = raw.status;
    else if (Array.isArray(raw) && raw[0]?.attribute_identifier_list) list = raw[0].attribute_identifier_list;
    else if (raw.attribute_identifier_list && Array.isArray(raw.attribute_identifier_list)) list = raw.attribute_identifier_list;
    else if (Array.isArray(raw)) list = raw;

    return list
      .map((a: any) => (typeof a === "object" ? (a.identifier || a.value || a.name || "") : a))
      .filter((s) => typeof s === "string" && s.length > 0);
  }, [fieldItemSuggestionsQuery.data]);

  const lineAttributeOptions = useMemo(() => {
    const raw: any = lineItemSuggestionsQuery.data;
    if (!raw) return [];
    let list: any[] = [];
    if (Array.isArray(raw.data)) list = raw.data;
    else if (Array.isArray(raw.status)) list = raw.status;
    else if (Array.isArray(raw) && raw[0]?.attribute_identifier_list) list = raw[0].attribute_identifier_list;
    else if (raw.attribute_identifier_list && Array.isArray(raw.attribute_identifier_list)) list = raw.attribute_identifier_list;
    else if (Array.isArray(raw)) list = raw;

    return list
      .map((a: any) => (typeof a === "object" ? (a.identifier || a.value || a.name || "") : a))
      .filter((s) => typeof s === "string" && s.length > 0);
  }, [lineItemSuggestionsQuery.data]);

  const verification: VerificationStatusMap = useMemo(() => {
    const raw = (homePayload as Record<string, unknown> | null)?.verification_status;
    if (!raw) return {};
    if (typeof raw === "string") return parseVerificationStatus(raw);
    return raw as VerificationStatusMap;
  }, [homePayload]);

  const actionContext = {
    workflow_id: workflowId,
    uploader,
    verification_component: verificationComponent,
    table_name: tableName,
  };

  const submitMutation = useMutation({
    // Template Engine (config_studio_tracking) finalizes via a dedicated
    // endpoint that takes a tighter payload — see the reference's
    // ToolsView.jsx onSubmit. OCR Tracking docs keep using the shared
    // /verification_submit endpoint.
    mutationFn: () =>
      isConfigStudio
        ? ocrApi.configStudioToolSubmit({
            processId,
            workflowId,
            verificationComponent,
            tableName,
          })
        : ocrApi.verificationSubmit(processId, actionContext),
    onSuccess: () => {
      toast.success("Verification submitted");
      queryClient.invalidateQueries({ queryKey: ["ocr", "tracking"] });
      // Both flows now hand off to the Business Rules step for the same
      // document after TV completion — `businessRulesPath` already
      // resolves to `/studio/templateEngine/business-rules` for
      // config_studio docs and `/ocr-tracking/business-rules` for OCR
      // Tracking docs, and `withTableName(...)` threads the right
      // `table_name` through so Business Rules / Final Checker can
      // continue to roundtrip back to the right inbox.
      const params = new URLSearchParams({ id: processId });
      if (workflowId) params.set("workflow_id", workflowId);
      if (uploader) params.set("uploader", uploader);
      if (filenameParam) params.set("filename", filenameParam);
      if (layoutIdParam) params.set("layout_id", layoutIdParam);
      navigate(`${businessRulesPath}?${withTableName(params).toString()}`, {
        state: {
          process_id: processId,
          layout_id: layoutIdParam,
          workflow_id: workflowId,
        },
      });
    },
    onError: (err) =>
      toast.error("Submit failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => ocrApi.verificationReject(processId, reason),
    onSuccess: () => {
      toast.success("Verification rejected");
      queryClient.invalidateQueries({ queryKey: ["ocr", "tracking"] });
      navigate(inboxPath);
    },
    onError: (err) =>
      toast.error("Reject failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      ocrApi.verificationReset({
        processId,
        collectionName:
          activeTab === "fielditem" ? "fielditem_content" : "lineitem_content",
        pageNumber: currentPage,
        verificationComponent: verificationComponent,
        tableName: tableName,
      }),
    onSuccess: () => {
      toast.success("Verification reset");
      setEdits({});
      setFieldEdits({});
      setRenames({});
      homeQuery.refetch();
      projectionQuery.refetch();
    },
    onError: (err) =>
      toast.error("Reset failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Mirrors ocr-ui's `updateFormValueFromPairs` (tools-file-preview.jsx). For
  // single-box mode `pair.value` is undefined and `pair.key` is the synthetic
  // left-half; the backend expects the full drawn area as `value` so it can
  // OCR the text and map it to `field_name`. Sending an empty `value: {}`
  // makes the new field never appear in the projection response.
  const normalizeFieldPairs = () => {
    const safeNormalize = (
      coords?: { x: number; y: number; w: number; h: number },
    ) => {
      if (!coords || typeof coords.x !== "number" || isNaN(coords.x)) {
        return {} as Record<string, number>;
      }
      return {
        xmin: Math.round(coords.x),
        ymin: Math.round(coords.y),
        xmax: Math.round(coords.x + coords.w),
        ymax: Math.round(coords.y + coords.h),
      };
    };

    return fieldRulePairs
      .filter((p) => p.field)
      .map((p) => {
        let keyBox: Record<string, number>;
        let valueBox: Record<string, number>;

        if (p.value) {
          // Separate-box: key and value were both drawn explicitly.
          keyBox = safeNormalize(p.key);
          valueBox = safeNormalize(p.value);
        } else if (!p.key || p.key.w === 0 || p.key.h === 0) {
          // Static value / only-value: no key, the whole drawn area is the value.
          keyBox = {};
          valueBox = safeNormalize(p.whole);
        } else {
          // Single box: try to refine via OCR words first — for a draw over
          // "Country of origin: INDIA" + field "Country of Origin", this
          // returns key = label words, value = "INDIA", which is far more
          // robust than letting the backend subtract a synthetic half-box.
          const refined = refineSingleBoxFromOcr(p.whole, p.field, ocrWords);
          if (refined) {
            keyBox = safeNormalize(refined.key);
            valueBox = safeNormalize(refined.value);
          } else {
            // Fallback: synthetic left-half key, full drawn area as value.
            keyBox = safeNormalize(p.key);
            valueBox = safeNormalize(p.whole);
          }
        }

        return {
          field_name: p.field,
          key: keyBox,
          value: valueBox,
        };
      });
  };

  const normalizeTableOverlay = () => {
    if (!tableOverlay) return null;
    return {
      x: Math.round(tableOverlay.x),
      y: Math.round(tableOverlay.y),
      width: Math.round(tableOverlay.width),
      height: Math.round(tableOverlay.height),
      rowHeights: tableOverlay.rowHeights.map(Math.round),
      colWidths: tableOverlay.colWidths.map(Math.round),
      headerMappings,
    };
  };

  /** Converts our modern tableOverlay into the legacy vertical/horizontal lines format for the manual/lineitem API. */
  const toLegacyTableBoundaries = () => {
    if (!tableOverlay) return "{}";
    const { x, y, width, height, rowHeights, colWidths } = tableOverlay;
    const headerH = rowHeights[0] || 45;

    const vLines: any[] = [];
    let curX = x;
    // vertical lines for columns
    [0, ...colWidths].forEach((w, i) => {
      curX += w;
      vLines.push({
        xmin: Math.round(curX),
        xmax: Math.round(curX + 1),
        ymin: Math.round(y),
        ymax: Math.round(y + height),
        field_name: headerMappings[`0_${i}`] || "",
      });
    });

    // Header-content divider — appears in BOTH header.horizontal_line
    // (as the header bottom) and content.horizontal_line (as the first
    // entry, defining where the content region begins). This shared
    // entry is required by the backend — leaving it out of `content`
    // makes the projection come back with empty tables. Matches
    // ocr-ui's tools-file-preview.jsx `headerContentDivider` pattern.
    const headerContentDivider = {
      xmin: Math.round(x),
      xmax: Math.round(x + width),
      ymin: Math.round(y + headerH),
      ymax: Math.round(y + headerH + 1),
    };

    const hHeaderLines = [
      { xmin: Math.round(x), xmax: Math.round(x + width), ymin: Math.round(y), ymax: Math.round(y + 1) },
      headerContentDivider,
    ];

    const hContentLines: any[] = [headerContentDivider];
    let curY = y + headerH;
    rowHeights.slice(1).forEach((h) => {
      curY += h;
      hContentLines.push({
        xmin: Math.round(x),
        xmax: Math.round(x + width),
        ymin: Math.round(curY),
        ymax: Math.round(curY + 1),
      });
    });

    return JSON.stringify({
      header: {
        vertical_line: vLines,
        horizontal_line: hHeaderLines,
      },
      content: {
        horizontal_line: hContentLines,
      },
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const isField = activeTab === "fielditem";
      const editPayload = Object.entries(isField ? fieldEdits : edits).map(
        ([key, value]) => ({ key, value }),
      );
      const renamePayload = isField
        ? Object.entries(renames).map(([key, newName]) => ({ key, newName }))
        : [];

      const payload: any = {
        ...actionContext,
        collection_name: isField ? "fielditem_content" : "lineitem_content",
        page_no: String(currentPage),
        edits: editPayload,
        renames: renamePayload,
      };

      if (toolsMode.fielditem === "rule") {
        payload.field_item_boxes = normalizeFieldPairs();
      }
      if (toolsMode.lineitem === "rule") {
        payload.line_item_boxes = normalizeTableOverlay();
      }

      return ocrApi.verificationSave(processId, payload);
    },
    onSuccess: () => {
      toast.success("Saved");
      if (activeTab === "fielditem") {
        setFieldEdits({});
        setRenames({});
      } else {
        setEdits({});
      }
      projectionQuery.refetch();
    },
    onError: (err) =>
      toast.error("Save failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const processMutation = useMutation({
    mutationFn: async () => {
      const isField = activeTab === "fielditem";
      const collection = isField ? "fielditem_content" : "lineitem_content";
      const tabKey: "fielditem" | "lineitem" = isField ? "fielditem" : "lineitem";

      // Reset the WS completion flag for this tab — anything left over
      // from a previous run is stale.
      extractionCompleteRef.current[tabKey] = false;

      // Snapshot the current projection so we can detect when the backend
      // has actually written new results (manual/* is async — the response
      // returns before the projection is updated). Without this we
      // sometimes refetch too early and the user sees stale data until
      // they hit Refresh manually.
      const beforeProjection = projectionQuery.data;
      const beforeKey = beforeProjection
        ? JSON.stringify(pickExtractedContent(beforeProjection))
        : "";

      if (isField) {
        const pagesPayload = JSON.stringify([{
          page_no: String(currentPage),
          boundary: normalizeFieldPairs(),
        }]);
        await ocrApi.manualFieldItem({
          processId,
          pages: pagesPayload,
          tableName,
          orgId: DEFAULT_ORG_ID,
        });
      } else {
        await ocrApi.manualLineItem({
          processId,
          pageNo: String(currentPage),
          boundaries: toLegacyTableBoundaries(),
          tableName,
          orgId: DEFAULT_ORG_ID,
        });
      }

      toast.info("Manual extraction triggered, refreshing content...");

      // Refresh the home metadata once (cheap, doesn't change on every poll).
      await ocrApi.verificationHome({
        processId,
        workflowId,
        uploader,
        verificationComponent,
        tableName,
      });

      // Refetch immediately (matches the legacy Promise.all behavior so
      // line-item extraction — which the backend writes synchronously
      // before manual/lineitem returns — lands on the first attempt),
      // then poll for slower async cases. The reference (ocr-ui
      // ToolsView.jsx) uses a WebSocket `process_complete` message and
      // doesn't have to wait this long; without that channel we need a
      // generous budget — line-item table extraction routinely takes
      // 10–30s on the backend before `select_projection` is populated.
      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      // Line items need more time than field items.
      const maxAttempts = isField ? 12 : 20;
      let changed = false;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) {
          // Gentle backoff capped at 2 s.
          const delay = Math.min(500 + attempt * 250, 2000);
          await sleep(delay);
        }
        // Socket fast-path: the WS handler flipped this when the backend
        // emitted process_complete for this tab. The cache is already
        // refetched in the handler, so we just need one more refetch here
        // to read it into `result` and then exit the loop.
        if (extractionCompleteRef.current[tabKey]) {
          const result = await projectionQuery.refetch();
          const ec = pickExtractedContent(result.data);
          if (ec !== undefined) {
            changed = true;
          }
          break;
        }
        // Invalidate so refetch actually hits the network instead of
        // returning the cached `beforeProjection` value.
        queryClient.invalidateQueries({
          queryKey: [
            "ocr",
            "verification",
            "projection",
            collection,
            processId,
            currentPage,
          ],
        });
        const result = await projectionQuery.refetch();
        const ec = pickExtractedContent(result.data);
        const afterKey = ec ? JSON.stringify(ec) : "";
        // Detect not just "any change" but "extraction populated
        // content". For line items, `{tables: []}` is a valid early-state
        // response that's technically different from "no data" but
        // doesn't mean the backend has finished — keep polling until
        // rows appear. For field items, the projection is typically an
        // OBJECT (`{Header: [...]}` or `{fielditem: {Header: [...]}}`)
        // — Array.isArray(ec) was always false there, so the polling
        // loop ran the full 12 attempts and the user saw "No fields
        // extracted yet" until the next refetch later in the function.
        const hasNonEmptyArrayDeep = (v: unknown): boolean => {
          if (Array.isArray(v)) return v.length > 0;
          if (v && typeof v === "object") {
            for (const inner of Object.values(v as Record<string, unknown>)) {
              if (Array.isArray(inner)) {
                if (inner.length > 0) return true;
              } else if (inner && typeof inner === "object") {
                if (hasNonEmptyArrayDeep(inner)) return true;
              }
            }
          }
          return false;
        };
        const hasContent = (() => {
          if (!ec || typeof ec !== "object") return false;
          if (Array.isArray(ec)) return ec.length > 0;
          const obj = ec as Record<string, unknown>;
          if (Array.isArray(obj.tables)) {
            return (obj.tables as unknown[]).some((t) => {
              const tobj = t as Record<string, unknown> | undefined;
              const rows = (tobj?.table_rows ?? tobj?.rows) as
                | unknown[]
                | undefined;
              return Array.isArray(rows) && rows.length > 0;
            });
          }
          // Field-item shapes — walk nested objects looking for any
          // non-empty array. Covers `{Header: [...]}`,
          // `{fielditem: {Header: [...]}}`, etc.
          return hasNonEmptyArrayDeep(obj);
        })();
        if (hasContent && afterKey !== beforeKey) {
          changed = true;
          break;
        }
      }

      // Always refresh coordinates and home view at the end; do home
      // refetch last so any layout_id / metadata updates are visible.
      await Promise.all([
        coordinatesQuery.refetch(),
        homeQuery.refetch(),
      ]);

      if (!changed) {
        // Surface this so the user knows the data may still be processing
        // rather than thinking the rule didn't take effect.
        toast.warning(
          "Processing is taking longer than expected — try Refresh in a moment.",
        );
      }
    },
    onSuccess: () => {
      toast.success("Processing complete");
      setEdits({});
      setFieldEdits({});
      setRenames({});
      // Reset only the *in-progress* draw state — leave the confirmed
      // pairs / table overlay in place so the user keeps seeing the boxes
      // they just defined (they map 1:1 to what the backend stored).
      setPendingFieldPair(null);
      setActiveFieldPairIndex(null);
    },
    onError: (err) =>
      toast.error("Process failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const toggleManualAdjustment = () => {
    const next = !manualMode[activeTab];
    setManualMode((prev) => ({ ...prev, [activeTab]: next }));
  };

  /**
   * Delete one or more field items via the dedicated backend endpoint
   * (`POST /verification/fielditem/delete`). The endpoint accepts a
   * `field_name` array and removes those entries server-side, so we
   * no longer need to walk the projection's extracted_content tree
   * client-side and re-serialize via verificationSave.
   *
   * Collection mirrors the active TV/BR mode — the cockpit defaults
   * to `fielditem_content` (TV); the BR Final Checker uses its own
   * page-level mutations and isn't affected here.
   */
  const deleteFieldsMutation = useMutation({
    mutationFn: async (
      fieldsToDelete: { group: string; field_name: string }[],
    ) => {
      if (fieldsToDelete.length === 0) return 0;
      const fieldNames = fieldsToDelete.map((f) => f.field_name);
      await ocrApi.deleteFieldItem({
        processId,
        pageNumber: currentPage,
        collectionName:
          verificationComponent === "BR"
            ? "br_fielditem_content"
            : "fielditem_content",
        fieldNames,
        verificationComponent,
      });
      return fieldsToDelete.length;
    },
    onSuccess: (count, fieldsToDelete) => {
      if (!count) return;
      toast.success(
        count === 1 ? "Field deleted" : `${count} fields deleted`,
      );
      projectionQuery.refetch();
      // Coordinates drive the numbered markers on the document image.
      // Without refetching, the marker for a just-deleted field stays
      // visible until the user changes page or tab.
      coordinatesQuery.refetch();
      // `fieldRulePairs` drives the coloured boxes / field-name labels
      // drawn over the document image. It's populated by the
      // loadCoordinates effect on page/tab/layout change, so a delete
      // alone won't shrink it — strip the deleted entries here so the
      // boxes disappear immediately instead of waiting for a reload.
      const deletedNames = new Set(fieldsToDelete.map((f) => f.field_name));
      setFieldRulePairs((prev) => prev.filter((p) => !deletedNames.has(p.field)));
    },
    onError: (err) =>
      toast.error("Delete failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  /**
   * Delete an entire line-item table (the "Delete Table" button in the
   * LINE ITEM tab). Hits `/verification/lineitem/delete` with
   * `record_instance: "table"` — matches the eroyal contract. Collection
   * follows the TV/BR split, same as `deleteFieldsMutation`.
   */
  const deleteLineItemTableMutation = useMutation({
    mutationFn: (tableIndex: number) =>
      ocrApi.deleteLineItemTable({
        processId,
        pageNumber: currentPage,
        collectionName:
          verificationComponent === "BR"
            ? "br_lineitem_content"
            : "lineitem_content",
        tableIndex,
        verificationComponent,
      }),
    onSuccess: () => {
      toast.success("Table deleted");
      setEdits({});
      projectionQuery.refetch();
    },
    onError: (err) =>
      toast.error("Delete table failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const clearMutation = useMutation({
    // Use the dedicated /verification_clear endpoint (matches ocr-ui's
    // `verificationClear`). The previous `verificationSave({clear:true})`
    // shape returns 422 — that flag isn't a valid combination on the
    // save endpoint.
    mutationFn: () =>
      ocrApi.verificationClear({
        processId,
        collectionName:
          activeTab === "fielditem" ? "fielditem_content" : "lineitem_content",
        pageNumber: currentPage,
        verificationComponent: verificationComponent,
      }),
    onSuccess: () => {
      toast.success("Page cleared");
      setEdits({});
      setFieldEdits({});
      setRenames({});
      projectionQuery.refetch();
    },
    onError: (err) =>
      toast.error("Clear failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const navigator = useDocumentNavigator(processId, tableName);

  const saveAndNext = async () => {
    const isField = activeTab === "fielditem";
    const editCount =
      Object.keys(isField ? fieldEdits : edits).length +
      (isField ? Object.keys(renames).length : 0);
    try {
      if (editCount > 0) {
        await saveMutation.mutateAsync();
      }
      if (navigator.next) {
        navigate(navigator.next.href);
      } else {
        toast("No next document — back to inbox");
        navigate(inboxPath);
      }
    } catch { }
  };

  if (!requestReady) {
    const missing: string[] = [];
    if (!processId) missing.push("id");
    if (!workflowId) missing.push("workflow_id");
    if (!uploader) missing.push("uploader");

    return (
      <PageShell title="Verification" subtitle="Missing context from inbox">
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
          <p>
            The verification cockpit needs <code className="rounded bg-white px-1">id</code>,{" "}
            <code className="rounded bg-white px-1">workflow_id</code> and{" "}
            <code className="rounded bg-white px-1">uploader</code> in the URL.
          </p>
          <p className="mt-3 text-xs text-gray-500">
            Missing: <strong>{missing.join(", ")}</strong>. Open the document
            from the OCR Tracking inbox so the link carries the full context.
          </p>
          <Link
            to={inboxPath}
            className="mt-4 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {isConfigStudio ? "Back to Template Engine" : "Back to inbox"}
          </Link>
        </div>
      </PageShell>
    );
  }

  const handleFieldRuleChange = (index: number, pair: FieldRulePair) => {
    setFieldRulePairs((prev) => {
      const next = [...prev];
      next[index] = pair;
      return next;
    });
  };

  const pageDisplayName = filenameParam || "Verification";

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-indigo-50/40 via-white to-violet-50/30 flex">
      <Sidebar />

      <div className="flex-1 min-w-0 ml-16 mr-4 mt-4 mb-2 overflow-hidden">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col border border-gray-100">
          <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              <Link
                to={inboxPath}
                className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                title={isConfigStudio ? "Back to Template Engine" : "Back to inbox"}
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div className="flex items-center rounded-md border border-gray-200 bg-white">
                <button
                  type="button"
                  disabled={!navigator.prev}
                  onClick={() => navigator.prev && navigate(navigator.prev.href)}
                  title={
                    navigator.prev
                      ? `Previous: ${navigator.prev.doc.filename}`
                      : "No previous document"
                  }
                  className="px-1.5 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="border-x border-gray-200 px-2 py-1 text-[11px] tabular-nums text-gray-600">
                  {navigator.position
                    ? `${navigator.position.index} / ${navigator.position.total}`
                    : "—"}
                </span>
                <button
                  type="button"
                  disabled={!navigator.next}
                  onClick={() => navigator.next && navigate(navigator.next.href)}
                  title={
                    navigator.next
                      ? `Next: ${navigator.next.doc.filename}`
                      : "No next document"
                  }
                  className="px-1.5 py-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-sm font-semibold text-gray-900">
                  {pageDisplayName}
                </h1>
                {(layoutIdParam || workflowId) ? (
                  <p className="truncate text-[11px] text-gray-500">
                    {layoutIdParam ? (
                      <span className="text-indigo-600 hover:underline">
                        #{layoutIdParam}
                      </span>
                    ) : null}
                    {layoutIdParam && workflowId ? <span className="mx-1">·</span> : null}
                    {workflowId ? (
                      <span className="font-mono">{workflowId}</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
              {homePayload?.status ? (
                <ExtractionStageChip
                  status={typeof homePayload.status === "string" ? (homePayload.status as string) : "—"}
                />
              ) : null}
              <VerificationStatusBadges status={verification} />
            </div>

            <div className="flex flex-shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1 py-1">
              <ToolbarButton icon={<Pencil className="h-3.5 w-3.5" />} label="Edit" disabled />
              <ToolbarButton icon={<StickyNote className="h-3.5 w-3.5" />} label="Note" disabled />
              <ToolbarSeparator />
              <ToolbarButton
                icon={<Eraser className="h-3.5 w-3.5" />}
                label="Clear"
                onClick={() => {
                  if (window.confirm(`Clear all edits on page ${currentPage}?`)) {
                    clearMutation.mutate();
                  }
                }}
                pending={clearMutation.isPending}
              />
              <ToolbarButton
                icon={<RotateCcw className="h-3.5 w-3.5" />}
                label="Reset"
                onClick={() => {
                  if (window.confirm("Reset all edits for this document?")) {
                    resetMutation.mutate();
                  }
                }}
                pending={resetMutation.isPending}
              />
              <ToolbarSeparator />
              <ToolbarButton
                icon={
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${homeQuery.isFetching || projectionQuery.isFetching
                      ? "animate-spin"
                      : ""
                      }`}
                  />
                }
                label="Refresh"
                onClick={() => {
                  homeQuery.refetch();
                  projectionQuery.refetch();
                  fieldItemSuggestionsQuery.refetch();
                  lineItemSuggestionsQuery.refetch();
                }}
              />
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              {(() => {
                const editCount =
                  activeTab === "fielditem"
                    ? Object.keys(fieldEdits).length
                    : Object.keys(edits).length;
                return (
                  <div className="inline-flex items-stretch overflow-hidden rounded-md border border-emerald-200 bg-white">
                    {/* <button
                      type="button"
                      onClick={() => saveMutation.mutate()}
                      disabled={editCount === 0 || saveMutation.isPending}
                      title={
                        editCount === 0
                          ? "No changes to save"
                          : `Save ${editCount} edit${editCount === 1 ? "" : "s"} on ${activeTab === "fielditem" ? "Field Item" : "Line Item"}`
                      }
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    >
                      <Save className="h-3.5 w-3.5" />
                      Save
                      {editCount > 0 ? (
                        <span className="ml-1 rounded-full bg-emerald-100 px-1.5 text-[10px] tabular-nums text-emerald-800">
                          {editCount}
                        </span>
                      ) : null}
                    </button> */}
                    {/* <button
                      type="button"
                      onClick={saveAndNext}
                      disabled={saveMutation.isPending || !navigator.next}
                      title={
                        navigator.next
                          ? `Save and open next: ${navigator.next.doc.filename}`
                          : "No next document in queue"
                      }
                      className="inline-flex items-center gap-1 border-l border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      Save &amp; next
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button> */}
                  </div>
                );
              })()}
              <button
                type="button"
                onClick={() => {
                  const reason = window.prompt("Reason for rejection (optional):") ?? "";
                  if (window.confirm(`Reject "${pageDisplayName}"?`)) {
                    rejectMutation.mutate(reason);
                  }
                }}
                disabled={rejectMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("Submit verification?")) submitMutation.mutate();
                }}
                disabled={submitMutation.isPending || homeQuery.isLoading}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                Proceed
              </button>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            <section className="relative flex flex-1 flex-col bg-gray-50">
              {manualMode[activeTab] ? (
                <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-900">
                  <span>
                    <strong>Manual adjustment</strong> is on.
                  </span>
                  <button
                    type="button"
                    onClick={toggleManualAdjustment}
                    className="rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
                  >
                    Turn off
                  </button>
                </div>
              ) : null}
              <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white/80 px-4 py-2 backdrop-blur">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => navigateToPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-0.5 rounded-md bg-gray-50 px-2 py-1 text-xs">
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isNaN(n)) {
                          navigateToPage(Math.max(1, Math.min(totalPages, n)));
                        }
                      }}
                      className="w-8 bg-transparent text-center font-semibold text-gray-900 tabular-nums focus:outline-none"
                    />
                    <span className="text-gray-400">/</span>
                    <span className="tabular-nums text-gray-600">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigateToPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <div className="mr-1 flex items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 p-0.5">
                    {/* "Original" view-mode is hidden for now per product
                        ask. The cleanView path (`viewMode === "original"`)
                        and the typed state value remain in place so we
                        can re-add the button later without rewiring. */}
                    {(
                      [
                        { id: "annotated", label: "Document" },
                        // { id: "original", label: "Original" },
                        { id: "text", label: "OCR text" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setViewMode(opt.id)}
                        className={`rounded px-2 py-1 text-[11px] font-medium transition ${viewMode === opt.id
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <ToolbarSeparator />
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(50, z - 10))}
                    disabled={viewMode === "text"}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom(100)}
                    disabled={viewMode === "text"}
                    className="rounded-md px-2 py-1 text-xs tabular-nums text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                  >
                    {zoom}%
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(200, z + 10))}
                    disabled={viewMode === "text"}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <ToolbarSeparator />
                  <button
                    type="button"
                    onClick={() => {
                      if (pageData?.image_path) window.open(pageData.image_path, "_blank");
                    }}
                    disabled={!pageData?.image_path}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div
                ref={docPan.ref}
                {...docPan.panHandlers}
                className={`flex flex-1 items-start justify-center overflow-auto p-6 ${
                  docPan.isPanning ? "cursor-grabbing select-none" : "cursor-grab"
                }`}
                onClick={() => { if (toolsMode.lineitem === 'rule') setTableConfirmed(false); }}
              >
                {homeQuery.isLoading ? (
                  <CenteredText>Loading verification…</CenteredText>
                ) : homeQuery.isError ? (
                  <ErrorBox
                    title="verification_home failed"
                    message={homeQuery.error instanceof Error ? homeQuery.error.message : null}
                    onRetry={() => homeQuery.refetch()}
                  />
                ) : viewMode === "text" ? (
                  <OcrTextPanel pageData={pageData} currentPage={currentPage} processId={processId} />
                ) : pageData?.image_path ? (
                  <DocumentImageWithHighlight
                    src={pageData.image_path}
                    alt={`Page ${currentPage}`}
                    highlightBoundary={activeBoundary ?? undefined}
                    naturalSizeHint={pageData.image_size}
                    zoom={zoom}
                    cleanView={viewMode === "original"}
                    markers={activeTab === "fielditem" ? markers : []}
                    activeMarkerFieldName={
                      activeTab === "fielditem" && active?.kind === "fielditem"
                        ? active.fieldName
                        : null
                    }
                    onImageRef={(img) => {
                      if (img) {
                        setImageNaturalDims({
                          width: img.naturalWidth,
                          height: img.naturalHeight,
                        });
                      }
                    }}
                    onImageDimensions={setImageNaturalDims}
                    tableOverlay={
                      activeTab === "lineitem" &&
                      (toolsMode.lineitem === "rule" || lineItemHasRowsOnPage)
                        ? tableOverlay
                        : null
                    }
                    onTableOverlayChange={(next) => {
                      setTableOverlay(next);
                      setTableConfirmed(false);
                    }}
                    headerMappings={headerMappings}
                    activeHeaderIndex={activeHeaderIndex}
                    onHeaderClick={(row, col) => setActiveHeaderIndex({ rowIndex: row, colIndex: col })}
                    onTableClick={() => setTableConfirmed(false)}
                    isRuleMode={
                      (activeTab === "fielditem" && toolsMode.fielditem === "rule") ||
                      (activeTab === "lineitem" && toolsMode.lineitem === "rule")
                    }
                    isFieldItemRuleMode={activeTab === "fielditem" && toolsMode.fielditem === "rule"}
                    fieldRulePairs={activeTab === "fielditem" ? fieldRulePairs : []}
                    activeFieldPairIndex={activeFieldPairIndex}
                    drawingSelection={drawingSelection}
                    onDrawingStart={handleDrawingStart}
                    onDrawingMove={handleDrawingMove}
                    onDrawingEnd={handleDrawingEnd}
                    onFieldRuleChange={handleFieldRuleChange}
                    pendingFieldPair={pendingFieldPair}
                    ocrWords={ocrWords}
                    onWordClick={handleWordClick}
                    onFieldRuleSelect={(idx) => {
                      setActiveFieldPairIndex(idx);
                      setFieldRulePairs(prev => {
                        const next = [...prev];
                        if (next[idx]) next[idx] = { ...next[idx], confirmed: false };
                        return next;
                      });
                    }}
                    renderTableOverlay={(table, activeCell, natural) => {
                      const topActions = !tableConfirmed ? (
                        <div
                          className="absolute flex items-center gap-2"
                          style={{
                            left: `${(table.x / natural.width) * 100}%`,
                            top: `${(table.y / natural.height) * 100}%`,
                            transform: "translateY(-40px)",
                          }}
                        >
                          <button
                            onClick={(e) => { e.stopPropagation(); confirmLineItemRule(); }}
                            className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-lg hover:bg-emerald-700"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Confirm Rule
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelLineItemRule(); }}
                            className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-gray-700 shadow-lg ring-1 ring-gray-300 hover:bg-gray-50"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </div>
                      ) : null;

                      let mappingPopover = null;
                      if (activeCell && activeCell.rowIndex === 0) {
                        const colX = table.x + table.colWidths.slice(0, activeCell.colIndex).reduce((a, b) => a + b, 0);
                        const cellW = table.colWidths[activeCell.colIndex];
                        const key = `${activeCell.rowIndex}_${activeCell.colIndex}`;
                        const attributes = activeTab === "fielditem" ? fieldAttributeOptions : lineAttributeOptions;

                        mappingPopover = (
                          <HeaderMappingPopover
                            anchor={{ x: colX, y: table.y, width: cellW, height: table.rowHeights[0] }}
                            imageNatural={natural}
                            currentValue={headerMappings[key] || ""}
                            options={attributes}
                            onConfirm={(selectedName) => {
                              setHeaderMappings((prev) => ({
                                ...prev,
                                [key]: selectedName,
                              }));
                              setActiveHeaderIndex(null);
                            }}
                            onCancel={() => setActiveHeaderIndex(null)}
                          />
                        );
                      }

                      return (
                        <>
                          {topActions}
                          {mappingPopover}
                        </>
                      );
                    }}
                    onMarkerClick={(m) => {
                      const w = m.keyBbox.xmax - m.keyBbox.xmin;
                      const h = m.keyBbox.ymax - m.keyBbox.ymin;
                      const boundary = `${m.keyBbox.xmin},${m.keyBbox.ymin},${w},${h}`;
                      setMarkerEdit({ fieldName: m.fieldName, keyBbox: m.keyBbox });
                      setActive({
                        kind: "fielditem",
                        boundary,
                        group: "",
                        fieldName: m.fieldName,
                        originalValue: "",
                      });
                    }}
                    renderOverlay={(anchor, natural) => {
                      if (markerEdit) {
                        const suggestions = activeTab === "fielditem" ? fieldAttributeOptions : lineAttributeOptions;
                        return (
                          <MarkerInlineEditor
                            anchor={anchor}
                            imageNatural={natural}
                            currentName={markerEdit.fieldName}
                            suggestions={suggestions}
                            onConfirm={(selectedName) => {
                              setRenames((prev) => ({
                                ...prev,
                                [markerEdit.fieldName]: selectedName,
                              }));
                              toast.success(`Renamed to "${selectedName}"`);
                              setMarkerEdit(null);
                              setActive(null);
                            }}
                            onCancel={() => {
                              setMarkerEdit(null);
                              setActive(null);
                            }}
                          />
                        );
                      }

                      // 2. Existing Active Highlight Popover
                      if (active) {
                        const isField = active.kind === "fielditem";
                        const currentEdits = isField ? fieldEdits : edits;
                        const key = isField
                          ? fieldEditKey(active.group, active.fieldName)
                          : editKey(active.tableName, active.rowIndex, active.column);
                        const displayValue = currentEdits[key] ?? active.originalValue;

                        return (
                          <FieldEditPopover
                            anchor={anchor}
                            imageNatural={natural}
                            fieldName={isField ? active.fieldName : active.column}
                            value={displayValue}
                            options={isField ? fieldAttributeOptions : lineAttributeOptions}
                            onValueChange={(next) => {
                              if (isField) {
                                setFieldEdits((prev) => ({ ...prev, [key]: next }));
                              } else {
                                setEdits((prev) => ({ ...prev, [key]: next }));
                              }
                            }}
                            onNameChange={(nextName) => {
                              // If they change the name from the popover, we should handle rename.
                              setRenames((prev) => ({
                                ...prev,
                                [isField ? active.fieldName : active.column]: nextName,
                              }));
                              toast.success(`Renamed to "${nextName}"`);
                            }}
                            onSave={() => setActive(null)}
                            onClear={() => {
                              if (isField) {
                                setFieldEdits((prev) => ({ ...prev, [key]: "" }));
                              } else {
                                setEdits((prev) => ({ ...prev, [key]: "" }));
                              }
                            }}
                            onClose={() => setActive(null)}
                          />
                        );
                      }

                      // 3. Field Rule Mapping Overlay (drawn box)
                      if (activeFieldPairIndex !== null && fieldRulePairs[activeFieldPairIndex]) {
                        const pair = fieldRulePairs[activeFieldPairIndex];
                        return (
                          <FieldRuleInlineEditor
                            anchor={anchor}
                            imageNatural={natural}
                            initialValue={pair.field}
                            options={[...fieldAttributeOptions, ...customFieldOptions]}
                            onConfirm={handleFieldRuleConfirm}
                            onCancel={handleFieldRuleCancel}
                            onAddNew={handleAddNewFieldAttribute}
                          />
                        );
                      }

                      return null;
                    }}
                  />
                ) : (
                  <NoImagePlaceholder
                    reason={
                      pageData
                        ? `Page ${currentPage} response is missing an "image_path" field.`
                        : pages
                          ? `Page ${currentPage} not found. Available: ${Object.keys(pages).join(", ")}.`
                          : "Response shape didn't include a `pages` object."
                    }
                  />
                )}
              </div>
            </section>

            {/* Right: extracted fields panel */}
            <aside className="flex h-full w-[420px] flex-shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white">
              {rulesViewer.open ? (
                /* View Rules — inline replacement for the ConfigPanel, just
                   like ocr-ui's ToolsView.jsx swaps in <LayoutEditor>. */
                <LayoutRulesViewer
                  layoutId={layoutIdParam}
                  processId={processId}
                  defaultTab={rulesViewer.tab}
                  coordinatesData={coordinatesQuery.data}
                  initialWorkflowId={
                    (typeof homePayload?.workflow_id === "string"
                      ? (homePayload.workflow_id as string)
                      : "") || workflowId
                  }
                  initialLayoutName={
                    (typeof homePayload?.customer_name === "string"
                      ? (homePayload.customer_name as string)
                      : "") ||
                    (typeof homePayload?.filename === "string"
                      ? (homePayload.filename as string)
                      : "") ||
                    filenameParam
                  }
                  onTabChange={(t) => {
                    setRulesViewer((prev) => ({ ...prev, tab: t }));
                    if (t === "fielditem" && activeTab !== "fielditem") {
                      setActiveTab("fielditem");
                    } else if (t === "lineitem" && activeTab !== "lineitem") {
                      setActiveTab("lineitem");
                    }
                  }}
                  onCancel={() =>
                    setRulesViewer((prev) => ({ ...prev, open: false }))
                  }
                  onRetriggerSuccess={async () => {
                    // Eager refresh — the backend processes
                    // `verification_retriger_lineitem` asynchronously
                    // and the WebSocket `process_complete` frame will
                    // refresh again when it actually finishes. Doing
                    // this here too means stale rows can't survive the
                    // case where the frame is delayed or dropped. We
                    // refetch both projection and coordinates because
                    // line-item retriggers can rewrite the layout's
                    // coordinate grid alongside the extracted data.
                    //
                    // POLLING — the retrigger API can return success
                    // before the backend has actually persisted the new
                    // extraction (it kicks off the LLM run and answers
                    // immediately). If we refetched once and closed the
                    // dialog, the results panel would render empty for
                    // a few seconds until the actual data lands. We
                    // poll the projection up to ~40s, checking for
                    // non-empty content that's different from the
                    // pre-retrigger snapshot, and only resolve once
                    // real data is back. Mirrors the cockpit's own
                    // processMutation polling pattern.

                    // Snapshot the projection BEFORE we start refetching
                    // so we can detect when something actually changed.
                    const beforeProjection = projectionQuery.data;
                    const beforeKey = beforeProjection
                      ? JSON.stringify(pickExtractedContent(beforeProjection))
                      : "";

                    // Coordinates load fast and don't need polling.
                    if (layoutIdParam) {
                      await coordinatesQuery.refetch();
                    }

                    // Helper to detect non-empty extracted content
                    // anywhere in the projection envelope (lineitem
                    // tables, fielditem groups, raw arrays).
                    const hasNonEmptyArrayDeep = (v: unknown): boolean => {
                      if (Array.isArray(v)) return v.length > 0;
                      if (v && typeof v === "object") {
                        for (const inner of Object.values(
                          v as Record<string, unknown>,
                        )) {
                          if (Array.isArray(inner)) {
                            if (inner.length > 0) return true;
                          } else if (inner && typeof inner === "object") {
                            if (hasNonEmptyArrayDeep(inner)) return true;
                          }
                        }
                      }
                      return false;
                    };

                    const sleep = (ms: number) =>
                      new Promise((r) => setTimeout(r, ms));
                    // ~40 seconds worst case (matches the processMutation
                    // line-item budget — line items take the longest).
                    const maxAttempts = 20;
                    for (let attempt = 0; attempt < maxAttempts; attempt++) {
                      if (attempt > 0) {
                        const delay = Math.min(500 + attempt * 250, 2000);
                        await sleep(delay);
                      }
                      // Force a network roundtrip — otherwise refetch
                      // can return the cached `beforeProjection` value.
                      queryClient.invalidateQueries({
                        queryKey: ["ocr", "verification", "projection"],
                        exact: false,
                      });
                      const result = await projectionQuery.refetch();
                      const ec = pickExtractedContent(result.data);
                      const afterKey = ec ? JSON.stringify(ec) : "";
                      // Stop as soon as the projection both changed AND
                      // carries real content. For lineitem-only retriggers
                      // the tables array becomes populated; for fielditem
                      // it's a non-empty group object.
                      const hasContent = (() => {
                        if (!ec || typeof ec !== "object") return false;
                        if (Array.isArray(ec)) return ec.length > 0;
                        const obj = ec as Record<string, unknown>;
                        if (Array.isArray(obj.tables)) {
                          return (obj.tables as unknown[]).some((t) => {
                            const tobj = t as
                              | Record<string, unknown>
                              | undefined;
                            const rows = (tobj?.table_rows ?? tobj?.rows) as
                              | unknown[]
                              | undefined;
                            return Array.isArray(rows) && rows.length > 0;
                          });
                        }
                        return hasNonEmptyArrayDeep(obj);
                      })();
                      if (hasContent && afterKey !== beforeKey) break;
                    }

                    queryClient.invalidateQueries({
                      queryKey: ["ocr", "verification"],
                      exact: false,
                    });
                  }}
                />
              ) : (
                <>
                  {/* Tabs */}
                  <div className="flex flex-shrink-0 border-b border-gray-200 bg-gray-50/60 px-3 pt-3">
                    <TabButton
                      active={activeTab === "fielditem"}
                      onClick={() => {
                        if (
                          isDirty &&
                          !window.confirm(
                            `Discard ${Object.keys(edits).length + Object.keys(fieldEdits).length} unsaved edit(s) and switch tabs?`,
                          )
                        ) {
                          return;
                        }
                        setActiveTab("fielditem");
                        setActiveDataCell(null);
                        setActive(null);
                        setEdits({});
                        setFieldEdits({});
                        setRenames({});
                        setPendingFieldPair(null);
                      }}
                    >
                      Field Item
                    </TabButton>
                    <TabButton
                      active={activeTab === "lineitem"}
                      onClick={() => {
                        if (
                          isDirty &&
                          !window.confirm(
                            `Discard ${Object.keys(edits).length + Object.keys(fieldEdits).length} unsaved edit(s) and switch tabs?`,
                          )
                        ) {
                          return;
                        }
                        setActiveTab("lineitem");
                        setActiveDataCell(null);
                        setActive(null);
                        setEdits({});
                        setFieldEdits({});
                        setRenames({});
                        setPendingFieldPair(null);
                      }}
                    >
                      Line Item
                    </TabButton>
                  </div>

                  {/* Panel content — separate components per tab. The whole
                  right panel scrolls together (config + results), not
                  just the results body. */}
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {activeTab === "fielditem" ? (
                      <FieldItemPanel
                        fields={tabData}
                        layoutId={layoutIdParam}
                        activeBoundary={activeBoundary}
                        onSelectField={(sel) => {
                          setActive({
                            kind: "fielditem",
                            boundary: sel.boundary,
                            group: sel.group,
                            fieldName: sel.fieldName,
                            originalValue: sel.value,
                          });
                        }}
                        edits={fieldEdits}
                        onEditField={(group, fieldName, value) => {
                          setFieldEdits((prev) => ({
                            ...prev,
                            [fieldEditKey(group, fieldName)]: value,
                          }));
                        }}
                        isLoading={
                          projectionQuery.isLoading ||
                          processMutation.isPending
                        }
                        onRetry={() => projectionQuery.refetch()}
                        mode={toolsMode.fielditem}
                        onModeChange={(m) =>
                          setToolsMode((prev) => ({ ...prev, fielditem: m }))
                        }
                        ruleFlags={ruleFlags.fielditem}
                        onRuleFlagsChange={(next) =>
                          setRuleFlags((prev) => ({ ...prev, fielditem: next }))
                        }
                        onProcess={() => processMutation.mutate()}
                        isProcessing={processMutation.isPending}
                        onManualAdjustment={() => {
                          if (!layoutIdParam) {
                            toast.error(
                              "Layout ID is required to open the editor.",
                            );
                            return;
                          }
                          // Manual Adjustment always navigates to Line Items
                          // — same behavior regardless of which panel it's
                          // invoked from.
                          setRulesViewer({ open: true, tab: "lineitem" });
                        }}
                        onViewRules={() => {
                          if (!layoutIdParam) {
                            toast.error("Layout ID is required to view rules.");
                            return;
                          }
                          setRulesViewer({ open: true, tab: "fielditem" });
                        }}
                        selectable
                        isDeleting={deleteFieldsMutation.isPending}
                        onDeleteFields={(fieldsToDelete) => {
                          if (fieldsToDelete.length === 0) return;
                          const confirm = window.confirm(
                            fieldsToDelete.length === 1
                              ? "Delete this field? This can't be undone."
                              : `Delete ${fieldsToDelete.length} fields? This can't be undone.`,
                          );
                          if (!confirm) return;
                          deleteFieldsMutation.mutate(fieldsToDelete);
                        }}
                      />
                    ) : (
                      <LineItemPanel
                        data={tabData}
                        layoutId={layoutIdParam}
                        isLoading={
                          projectionQuery.isLoading ||
                          processMutation.isPending
                        }
                        onRetry={() => projectionQuery.refetch()}
                        mode={toolsMode.lineitem}
                        onModeChange={(m) =>
                          setToolsMode((prev) => ({ ...prev, lineitem: m }))
                        }
                        ruleFlags={ruleFlags.lineitem}
                        onRuleFlagsChange={(next) =>
                          setRuleFlags((prev) => ({ ...prev, lineitem: next }))
                        }
                        onProcess={() => processMutation.mutate()}
                        isProcessing={processMutation.isPending}
                        onManualAdjustment={() => {
                          if (!layoutIdParam) {
                            toast.error(
                              "Layout ID is required to open the editor.",
                            );
                            return;
                          }
                          setRulesViewer({ open: true, tab: "lineitem" });
                        }}
                        onViewRules={() => {
                          if (!layoutIdParam) {
                            toast.error("Layout ID is required to view rules.");
                            return;
                          }
                          setRulesViewer({ open: true, tab: "lineitem" });
                        }}
                        activeCell={activeDataCell}
                        onSelectCell={(cell) => {
                          setActiveDataCell({
                            tableName: cell.tableName,
                            rowIndex: cell.rowIndex,
                            column: cell.column,
                            boundary: cell.boundary,
                          });
                          setActive({
                            kind: "lineitem",
                            boundary: cell.boundary || "",
                            tableName: cell.tableName,
                            rowIndex: cell.rowIndex,
                            column: cell.column,
                            originalValue: cell.value,
                          });
                        }}
                        edits={edits}
                        onEditCell={(tableName, rowIndex, column, value) => {
                          setEdits((prev) => ({
                            ...prev,
                            [editKey(tableName, rowIndex, column)]: value,
                          }));
                        }}
                        selectedRow={selectedRow}
                        selectedColumn={selectedColumn}
                        onRuleSelectionChange={handleRuleSelectionChange}
                        tableFallback={lineItemPreview}
                        hideRowSelection
                        onDeleteTable={(_name, tableIndex) => {
                          if (
                            window.confirm(
                              "Delete this table? This removes all rows in the table and cannot be undone.",
                            )
                          ) {
                            deleteLineItemTableMutation.mutate(tableIndex);
                          }
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Lavender connecting line: active field row → bbox on document.
          Targets the rendered bbox SVG rect via [data-active-bbox], so the
          arrow always lands precisely on the bbox edge. */}
      {active && active.boundary ? (
        <FieldConnectingLine
          sourceSelector="[data-active-field='true']"
          targetSelector="[data-active-bbox='true']"
          changeKey={active.boundary}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- helpers


function ToolbarButton({
  icon,
  label,
  onClick,
  disabled,
  pending,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  pending?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      title={label}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-white hover:text-gray-900 hover:shadow-sm disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:shadow-none"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <span className="mx-0.5 h-4 w-px bg-gray-200" />;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px flex-1 px-3 pb-2.5 pt-1.5 text-xs font-semibold transition-colors ${active
        ? "text-indigo-700"
        : "text-gray-500 hover:text-gray-700"
        }`}
    >
      {children}
      <span
        className={`absolute inset-x-3 bottom-0 h-0.5 rounded-full transition-colors ${active ? "bg-indigo-600" : "bg-transparent"
          }`}
      />
    </button>
  );
}

function PageShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
              {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : null}
            </div>
          </header>
          <div className="flex-1 p-6 overflow-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}

function CenteredText({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-gray-500">
      <div>{children}</div>
    </div>
  );
}

function NoImagePlaceholder({ reason }: { reason: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md rounded-2xl border border-dashed border-gray-300 bg-white px-8 py-12 text-center text-gray-500">
        <FileText className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm font-medium">No image preview</p>
        <p className="mt-1 text-xs">{reason}</p>
      </div>
    </div>
  );
}

function ErrorBox({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string | null;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto max-w-md rounded-md border border-rose-200 bg-rose-50 p-4 text-rose-700">
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4" />
        {title}
      </div>
      {message ? <p className="mt-1 text-sm text-rose-600">{message}</p> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

